// src/lib/memoryVerse.js
// ─────────────────────────────────────────────────────────────────────────────
//  Pure logic for the Memory Verse system (no React, no storage).
//
//  Method (research-backed):
//    1. Encode   — read the verse aloud a few times.
//    2. Cloze    — recall it with progressively more words hidden (25/50/75/100%).
//    3. Retrieve — test from memory (type it, or say it aloud and self-grade).
//    4. Space    — Leitner boxes: a pass moves the verse to a longer interval,
//                  a miss sends it back so it is reviewed sooner.
//
//  Verse shape:
//    { id, reference, text, createdAt, box, nextReview, lastReviewed, reviews, passes }
//    box 0 = still learning, 1-5 = memorized with growing review intervals.
// ─────────────────────────────────────────────────────────────────────────────

/** Days until next review for each Leitner box. Box 0 is "due again today". */
export const INTERVALS = [0, 1, 3, 7, 14, 30];
export const MAX_BOX = INTERVALS.length - 1;

/** Share of words that must be recalled in order to count a typed test as a pass. */
export const PASS_THRESHOLD = 0.9;

/** Fraction of words hidden at each learn step (step 0 = read aloud, full text). */
export const LEARN_LEVELS = [0, 0.25, 0.5, 0.75, 1];

// ── Dates (local calendar days, YYYY-MM-DD) ──────────────────────────────────
export function dayKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function shiftDay(iso, delta) {
  const [y, m, d] = iso.split('-').map(Number);
  return dayKey(new Date(y, m - 1, d + delta));
}

// ── Verse records ────────────────────────────────────────────────────────────
export function cleanText(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

export function newVerse({ reference, text, translation = '' }, today = dayKey()) {
  return {
    id: `${Date.now()}${Math.floor(Math.random() * 1000)}`,
    reference: cleanText(reference),
    text: cleanText(text),
    translation: String(translation || '').trim(),
    createdAt: new Date().toISOString(),
    box: 0,
    nextReview: today,
    lastReviewed: null,
    reviews: 0,
    passes: 0,
  };
}

export function statusOf(verse) {
  if (!verse || verse.box === 0) return 'learning';
  return verse.box >= 4 ? 'mastered' : 'memorized';
}

export function isDue(verse, today = dayKey()) {
  return !!verse && (verse.nextReview || '') <= today;
}

/** Due verses, most overdue first, then least-learned first. */
export function dueVerses(verses, today = dayKey()) {
  return (Array.isArray(verses) ? verses : [])
    .filter(v => isDue(v, today))
    .sort((a, b) =>
      (a.nextReview || '').localeCompare(b.nextReview || '') || (a.box - b.box)
    );
}

export function pickTodaysVerse(verses, today = dayKey()) {
  return dueVerses(verses, today)[0] || null;
}

/** Returns the updated verse after a recall attempt. */
export function applyResult(verse, passed, today = dayKey()) {
  let box = verse.box || 0;
  if (passed) box = Math.min(box + 1, MAX_BOX);
  else box = box > 1 ? 1 : box;            // a miss drops a memorized verse back to box 1
  return {
    ...verse,
    box,
    nextReview: shiftDay(today, INTERVALS[box]),
    lastReviewed: today,
    reviews: (verse.reviews || 0) + 1,
    passes: (verse.passes || 0) + (passed ? 1 : 0),
  };
}

export function describeNextReview(verse, today = dayKey()) {
  const days = INTERVALS[verse.box] ?? 0;
  if (days === 0) return 'again today';
  if (days === 1) return 'tomorrow';
  if (verse.nextReview && verse.nextReview <= today) return 'today';
  return `in ${days} days`;
}

// ── Words & hiding ───────────────────────────────────────────────────────────
export const tokens = (text) => cleanText(text).split(' ').filter(Boolean);

export const normalizeWord = (w) => String(w).toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

// ── Forgiving word match (speech recognition often mishears old English) ─────
const ALIASES = {
  thee: ['the', 'they'], thou: ['now', 'how'], thy: ['thigh', 'the', 'thai'],
  thine: ['nine', 'mine'], hath: ['have', 'has', 'half'], hast: ['has', 'haste'],
  doth: ['does', 'dust'], saith: ['say', 'says', 'safe'], ye: ['you', 'he', 'yeah'],
  unto: ['onto'], shalt: ['shall', 'shot'], wilt: ['will'], art: ['are'],
};

function editDistance(a, b) {
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
}

const stemOld = (w) => (w.length > 6 ? w.replace(/(eth|est)$/, '') : w);

/** True when `spoken` is close enough to the verse word `target` to count as recalled. */
export function wordsMatch(target, spoken) {
  if (target === spoken) return true;
  if (Object.hasOwn(ALIASES, target) && ALIASES[target].includes(spoken)) return true;
  const sx = stemOld(target);
  const sy = stemOld(spoken);
  const len = Math.min(sx.length, sy.length);
  const tolerance = len >= 9 ? 2 : len >= 5 ? 1 : 0;
  return editDistance(sx, sy) <= tolerance;
}

function hash(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/**
 * Which words are hidden at a given fraction. The order is fixed per verse (seeded),
 * so words hidden at 25% stay hidden at 50% and 75% — the verse only ever gets harder.
 */
export function hideMask(count, fraction, seed) {
  const order = Array.from({ length: count }, (_, i) => i)
    .sort((a, b) => hash(`${seed}:${a}`) - hash(`${seed}:${b}`));
  const hideN = Math.ceil(count * fraction);
  const mask = new Array(count).fill(false);
  for (let k = 0; k < hideN; k++) mask[order[k]] = true;
  return mask;
}

/** "word," -> "____," (punctuation kept). With firstLetter, "word," -> "w___,". */
export function maskToken(token, firstLetter = false) {
  let keptFirst = false;
  return Array.from(token).map(ch => {
    if (!/[\p{L}\p{N}]/u.test(ch)) return ch;
    if (firstLetter && !keptFirst) { keptFirst = true; return ch; }
    return '_';
  }).join('');
}

// ── Grading a typed recall ───────────────────────────────────────────────────
/**
 * Compares typed text with the verse by word order (longest common subsequence).
 * Case, punctuation and curly/straight apostrophes are ignored.
 * Returns { matched: boolean[] (per verse token), accuracy, passed }.
 */
export function compareRecall(target, typed) {
  const t = tokens(target);
  const tNorm = t.map(normalizeWord);
  const scoreable = tNorm.map((w, i) => (w ? i : -1)).filter(i => i >= 0);
  const a = scoreable.map(i => tNorm[i]);
  const b = tokens(typed).map(normalizeWord).filter(Boolean);

  const matched = t.map((_, i) => !tNorm[i]);           // pure-punctuation tokens never count against you
  if (a.length === 0) return { matched, accuracy: 1, passed: true };

  const dp = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = wordsMatch(a[i - 1], b[j - 1]) ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  let i = a.length, j = b.length;
  while (i > 0 && j > 0) {
    if (wordsMatch(a[i - 1], b[j - 1])) { matched[scoreable[i - 1]] = true; i--; j--; }
    else if (dp[i - 1][j] >= dp[i][j - 1]) i--;
    else j--;
  }
  const accuracy = dp[a.length][b.length] / Math.max(a.length, b.length);
  return { matched, accuracy, passed: accuracy >= PASS_THRESHOLD };
}

// ── Starter verses (KJV, public domain) ──────────────────────────────────────
export const STARTER_VERSES = [
  { reference: 'John 10:27', text: 'My sheep hear my voice, and I know them, and they follow me.' },
  { reference: 'Psalm 119:11', text: 'Thy word have I hid in mine heart, that I might not sin against thee.' },
  { reference: 'John 3:16', text: 'For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life.' },
  { reference: 'Proverbs 3:5-6', text: 'Trust in the LORD with all thine heart; and lean not unto thine own understanding. In all thy ways acknowledge him, and he shall direct thy paths.' },
  { reference: 'Philippians 4:13', text: 'I can do all things through Christ which strengtheneth me.' },
  { reference: 'Joshua 1:9', text: 'Have not I commanded thee? Be strong and of a good courage; be not afraid, neither be thou dismayed: for the LORD thy God is with thee whithersoever thou goest.' },
  { reference: '2 Timothy 2:2', text: 'And the things that thou hast heard of me among many witnesses, the same commit thou to faithful men, who shall be able to teach others also.' },
  { reference: 'Romans 12:2', text: 'And be not conformed to this world: but be ye transformed by the renewing of your mind, that ye may prove what is that good, and acceptable, and perfect, will of God.' },
];